/**
 * AudioPipeline — single entry point for audio processing across all Timbrica tools.
 *
 * Every tool that does pitch-shift / time-stretch / MP3-encode calls this module.
 * Benefits:
 *   - One place to pick engines (via `window.__AUDIO_CONFIG` injected by the layout)
 *   - One place to define fallback chains (runtime)
 *   - One place to emit telemetry
 *   - Kill-switch without redeploy: flip .env → AUDIO_ENGINE_* → reload php-fpm
 *
 * Usage:
 *   // Pitch shift (and/or time stretch) an AudioBuffer
 *   const shifted = await AudioPipeline.stretch(audioBuffer, {
 *       semitones: -3,            // pitch change in semitones (default 0)
 *       rate: 1,                  // time stretch factor, 0.5=slower, 2=faster (default 1)
 *       preserveFormants: true,   // keep voice character on big shifts (default false)
 *       slug: 'pitch',            // tool slug for telemetry
 *       onProgress: pct => {}     // 0..1
 *   });
 *
 *   // Encode an AudioBuffer or WAV Blob → MP3 Blob
 *   const mp3 = await AudioPipeline.encodeMP3(audioBufferOrWav, {
 *       bitrate: 192,
 *       slug: 'trim'
 *   });
 *
 * Engine selection (from `window.__AUDIO_CONFIG`):
 *   pitch_shift: 'signalsmith' | 'legacy' (FFmpeg asetrate+atempo)
 *   mp3_encoder: 'lamejs'      | 'legacy' (FFmpeg libmp3lame)
 */
(function(){
'use strict';

/* Метка «эта выдача оплачена токенами» для границы exec в ffmpeg-processor.js.
 Имя одно на оба файла — оно и есть контракт; сторож PaidOutputIsNotClampedTest
 держит обе стороны. */
var PAID_ARG = '-tmb_paid';

function cfg(key, fallback){
    try {
        var c = window.__AUDIO_CONFIG;
        if (c && typeof c[key] === 'string') return c[key];
    } catch(_){}
    return fallback;
}

function report(slug, action, payload){
    try {
        if (typeof window.__ae === 'function') {
            var data = Object.assign({ slug: slug || 'unknown', action: action }, payload || {});
            window.__ae('feature_use', data);
        }
    } catch(_){}
}

// ============================================================
// ensureFFmpegProcessorScript — lazy-load /js/ffmpeg-processor.js when the
// page didn't statically include it.
//
// Why this exists: 6 of the 15 tools that call AudioPipeline.stretch /
// encodeMP3 (text-to-speech, ai-music-cleaner, audio-inspector, dtmf-generator,
// kalimba-online, rodent-repellent) do NOT push the ffmpeg-processor.js
// <script> tag. On those pages window.FFmpegProcessor is the timbrica-core
// __sharedModule stub (layouts/app.blade.php:3119-3144), whose .init()
// returns a deferred promise that rejects after 15 s with "module never
// loaded (15s timeout)".
//
// When Signalsmith fails (~10% on first-visit cohorts where the worker
// script is truncated by an ISP/AV proxy or blocked by CSP/AdBlock), the
// stretch chain falls through to stretchViaFFmpeg → FFmpegProcessor.init()
// → 15-second dead-end + visible "download failed" toast. We caught this
// 2026-06-03 as 33 pipeline_stretch_failed{err:'module never loaded'} /
// day on /text-to-speech alone, all caused by the Signalsmith swap exposing
// a latent fallback gap that Rubber Band had been masking.
//
// The fix: before the FFmpeg fallback path runs, inject the real script
// from the audio-pipeline side. Tools that ship ffmpeg-processor.js
// eagerly are unaffected (fast-path returns immediately).
// ============================================================
var _ffmpegProcessorLoadPromise = null;

function _hasRealFFmpegProcessor(){
    try {
        var f = window.FFmpegProcessor;
        // The shared-module stub installs { __isStub: true, init: deferred-promise, ... }.
        // The real module (ffmpeg-processor.js:272) assigns a plain object with a
        // callable init() and NO __isStub flag.
        return !!(f && !f.__isStub && typeof f.init === 'function');
    } catch (_) { return false; }
}

function ensureFFmpegProcessorScript(slug){
    // Fast path: real module already on the page (static @light <script>,
    // earlier lazy-load this page-life, or BFCache restore).
    if (_hasRealFFmpegProcessor()) {
        report(slug, 'pipeline_ffmpeg_already_real', {});
        return Promise.resolve(true);
    }

    // Shared in-flight promise — two concurrent stretchViaFFmpeg /
    // encodeMP3ViaFFmpeg callers must NOT both inject a <script> tag. The
    // ffmpeg-processor.js IIFE (lines 60→715) has no top-level idempotency
    // guard; re-executing it reassigns window.FFmpegProcessor with a fresh
    // closure and orphans any in-flight init() against the previous instance.
    if (_ffmpegProcessorLoadPromise) return _ffmpegProcessorLoadPromise;

    var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    report(slug, 'pipeline_ffmpeg_lazy_load_start', {});

    _ffmpegProcessorLoadPromise = new Promise(function(resolve){
        var SRC = '/js/ffmpeg-processor.js';
        // Dispatched by __drainSharedModule at app.blade.php:3159 after the
        // real module is bound AND any queued stub.init() has been replayed.
        var DRAIN_EVENT = 'FFmpegProcessor:ready';
        // Post-onload grace for the drain to land — same tick in practice
        // but microtask ordering vs appendChild varies across browsers.
        var POST_LOAD_GRACE_MS = 1500;
        // Hard ceiling — covers SW serving a stalled revalidation, hung TCP,
        // or main-thread starvation preventing a deferred script from
        // executing. > the layout stub's 15 s so the layout "module never
        // loaded" rejection lands first with the cleaner message.
        var HARD_TIMEOUT_MS = 20000;

        var settled = false;
        var hardTimer = null;
        var graceTimer = null;
        var drainListener = null;
        var scriptEl = null;
        var weCreatedTheTag = false; // DEFECT 1: only attach load/error listeners if we created the tag.

        function ms(){
            var t = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
            return Math.round(t - t0);
        }

        function cleanup(){
            if (hardTimer)  { clearTimeout(hardTimer);  hardTimer  = null; }
            if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
            if (drainListener) {
                try { document.removeEventListener(DRAIN_EVENT, drainListener); } catch (_) {}
                drainListener = null;
            }
        }

        function succeed(){
            if (settled) return;
            settled = true;
            cleanup();
            report(slug, 'pipeline_ffmpeg_lazy_load_ok', { ms: ms() });
            resolve(true);
        }

        function fail(action, extra){
            if (settled) return;
            settled = true;
            cleanup();
            // DEFECT 3: mark the script element so the next retry knows to
            // inject a fresh one instead of waiting for a load event that
            // will never re-fire on this already-loaded tag.
            if (scriptEl) {
                try { scriptEl.setAttribute('data-ffp-loaded', '1'); } catch (_) {}
            }
            var payload = Object.assign({ ms: ms() }, extra || {});
            report(slug, action, payload);
            // Clear the cache so the NEXT user gesture retries — sticky
            // failure caching would block recovery from a transient blip.
            _ffmpegProcessorLoadPromise = null;
            resolve(false);
        }

        // Primary low-latency wakeup. Attached BEFORE we touch any DOM so a
        // racing drain (from an in-flight static tag that completes during
        // this call) doesn't slip through. The function-entry fast-path
        // check already covered the "already drained" case.
        drainListener = function(){
            if (_hasRealFFmpegProcessor()) succeed();
        };
        try { document.addEventListener(DRAIN_EVENT, drainListener, { once: false }); } catch (_) {}

        // Re-check after listener attach: covers the narrow race where an
        // existing tag finished draining between the fast-path check and now.
        if (_hasRealFFmpegProcessor()) { succeed(); return; }

        // Reuse an existing <script src="...ffmpeg-processor.js"> if one is
        // present AND its load lifecycle hasn't completed yet. This is the
        // case on FFmpeg-eager tool pages (audio-compressor + ~80 others)
        // where the static <script @cspNonce defer src="@light(...)">
        // fetch could still be in flight when audio-pipeline runs.
        //
        // DEFECT 3 guard: if a previous lazy-load completed but failed to
        // install the real module (Yandex/AV-proxy mid-body truncation —
        // the canonical class per app.blade.php:3111-3113), the dead tag
        // sits in <head> forever. The HTML spec fires the script `load`
        // event only ONCE per element, so reusing the dead tag would loop
        // 20 s every retry. Detect via our data-ffp-loaded marker and
        // force a fresh injection with a cache-buster.
        try {
            var existing = document.querySelector('script[src*="ffmpeg-processor.js"]');
            if (existing) {
                var alreadyLoaded = existing.getAttribute('data-ffp-loaded') === '1';
                if (alreadyLoaded && !_hasRealFFmpegProcessor()) {
                    existing = null; // force fresh injection below
                } else {
                    scriptEl = existing;
                }
            }
        } catch (_) {}

        if (!scriptEl) {
            try {
                scriptEl = document.createElement('script');
                // Cache-buster only fires when we're retrying after a known-
                // dead tag (which we just removed from `existing` above). On
                // the first injection the bare URL is fine — SW SWR will
                // serve cache + revalidate.
                var hadDeadTag = !!document.querySelector('script[src*="ffmpeg-processor.js"][data-ffp-loaded="1"]');
                scriptEl.src = SRC + (hadDeadTag ? '?retry=' + Date.now() : '');
                scriptEl.async = false; // preserve any queued-init ordering
                scriptEl.setAttribute('data-lazy-by', 'audio-pipeline');
                weCreatedTheTag = true;
                (document.head || document.documentElement).appendChild(scriptEl);
            } catch (e) {
                fail('pipeline_ffmpeg_lazy_load_failed', {
                    err: ('inject_' + ((e && e.message) || 'unknown')).substring(0, 180)
                });
                return;
            }
        }

        // DEFECT 1: only attach load/error listeners to the tag WE created.
        // The static blade tag at FFmpeg-eager pages carries an inline
        //   onerror="this.onerror=null;this.src='@lightFallback(...)'"
        // that swaps to a CDN fallback on primary 404. Our addEventListener
        // would synchronously fire BEFORE the inline handler's swap can
        // resolve, surfacing a false "FFmpeg engine unavailable" toast on
        // every transient CDN blip. For reused-existing tags we rely on
        // drain-event + hard-timeout instead.
        if (weCreatedTheTag) {
            scriptEl.addEventListener('load', function onLoad(){
                if (_hasRealFFmpegProcessor()) { succeed(); return; }
                // Truncated-IIFE case (browser silently swallows mid-file
                // SyntaxError, never reaches drain at line 1634): post-load
                // grace then mark the tag dead via fail().
                graceTimer = setTimeout(function(){
                    if (_hasRealFFmpegProcessor()) succeed();
                    else fail('pipeline_ffmpeg_lazy_load_post_load_no_global', {});
                }, POST_LOAD_GRACE_MS);
            });
            scriptEl.addEventListener('error', function onError(){
                fail('pipeline_ffmpeg_lazy_load_failed', { err: 'script_error' });
            });
        }

        hardTimer = setTimeout(function(){
            if (_hasRealFFmpegProcessor()) succeed();
            else fail('pipeline_ffmpeg_lazy_load_failed', { err: 'timeout' });
        }, HARD_TIMEOUT_MS);
    });

    return _ffmpegProcessorLoadPromise;
}

/**
 * AudioBuffer → WAV blob.
 *
 * @param {AudioBuffer} buffer
 * @param {number} [bits] 16 (default) · 24 · 32. 32 writes IEEE float — Web
 *   Audio samples ARE float32, so that path is bit-exact with no quantization.
 * @param {{dither?: boolean}} [opts] dither — TPDF dither on the 16-bit path
 *   (±1 LSB triangular): the right thing for a FINAL file made from a float
 *   mix, wrong for material that will be summed again (stems — measured
 *   2026-10-28: four dithered stems sum 9.6 dB noisier than four rounded
 *   ones), so it is the caller's call and off by default; off means
 *   round-half-even, not truncation. Either way audio already on the 16-bit
 *   grid comes back bit-exact (pcm16Quantizer). 24-bit's floor (−144 dB) needs
 *   none.
 *
 * Why more than 16 exists (bug report 2026-07-30, /equalizer): a 24-bit source
 * came out 16-bit, and the bit-depth picker the tool already had was HIDDEN for
 * lossless formats precisely because this writer could only emit 16-bit —
 * FFmpeg was then handed a 16-bit WAV, so asking it for `-sample_fmt s32`
 * could not bring the resolution back. The choice must be honoured here or not
 * offered at all.
 *
 * More than two channels go out as WAVE_FORMAT_EXTENSIBLE with a speaker mask
 * (2026-10-09, «5.1 zur Weiterverarbeitung» on /de/equalizer). A plain header
 * says «6 channels» and nothing about which speaker is which; Microsoft's
 * format note requires the extensible form above stereo, FFmpeg writes it, and
 * a DAW or an authoring tool reads the mask to place the channels. The masks
 * are the ones Chromium's own decoder accepts (5.1 = 0x60F, 7.1 = 0x63F —
 * measured 2026-09-13: it refuses the 0x3F «5.1 back» variant), so a file this
 * writer makes can always be opened again on the site. Channel order is the
 * mask's bit order — FL FR FC LFE then the surround pairs — which is the order
 * UpmixModule and FFmpeg already produce. Mono and stereo keep the plain
 * 44-byte header, byte-identical to before.
 */
/* The PCM goes into the Blob in pieces of WAV_PIECE_FRAMES frames (1 MB of 16-bit stereo), never
   as one buffer of the whole file. A 4-minute stereo song at 48 kHz was a single 45 MB block, and a
   Windows machine with 2 GB had no such block left after the separation on /vocal-remover: the WAV
   fallback after a failed MP3 died exactly like the MP3, and the person got no file at all — ten
   tries in two minutes (Sentry TIMBRICA-FRONTEND-NA, 30.09.2026). The bytes are the same as before
   (tests/js/audio-pipeline-wav-in-pieces.test.mjs); the browser joins the parts itself. */
var WAV_PIECE_FRAMES = 1 << 18;

/* reader(c, from, n) → channel c, frames [from, from + n), indexed from 0. A real AudioBuffer (or a
   shim with getChannelData) lends its own memory; an AudioBuffer-shaped VIEW (AudioPost.mixView)
   has no whole channel to lend — it computes the range, into a scratch array reused per piece. */
function pcmReader(buffer){
    var own = typeof buffer.getChannelData === 'function', chans = [], scratch = [];
    for (var c = 0; c < buffer.numberOfChannels; c++) { chans.push(own ? buffer.getChannelData(c) : null); scratch.push(null); }
    return function(c, from, n){
        if (own) return chans[c].subarray(from, from + n);
        if (!scratch[c] || scratch[c].length < n) scratch[c] = new Float32Array(n);
        var a = scratch[c].length === n ? scratch[c] : scratch[c].subarray(0, n);
        buffer.copyFromChannel(a, c, from);
        return a;
    };
}

// Speaker masks by channel count (KSAUDIO_SPEAKER_* «surround» variants).
// Same contract as OutputSink.wavHeader (tests/js/wav-header-parity.test.mjs).
var WAV_CHANNEL_MASK = { 3: 0x7, 4: 0x33, 5: 0x607, 6: 0x60F, 7: 0x70F, 8: 0x63F, 12: 0x2D63F };

/* RIFF/WAVE header: 44 bytes (PCM or IEEE float tag) up to stereo, 68 bytes
   (WAVE_FORMAT_EXTENSIBLE + mask + SubFormat GUID) above it. */
function wavHeader(numCh, sr, bits, isFloat, dataLen){
    var ext = numCh > 2, fmtLen = ext ? 40 : 16, headLen = 20 + fmtLen + 8;
    var ba = numCh * (bits / 8), ab = new ArrayBuffer(headLen), v = new DataView(ab);
    function ws(o, s){ for (var k = 0; k < s.length; k++) v.setUint8(o + k, s.charCodeAt(k)); }
    ws(0, 'RIFF'); v.setUint32(4, headLen - 8 + dataLen, true); ws(8, 'WAVE');
    ws(12, 'fmt '); v.setUint32(16, fmtLen, true);
    // Float PCM must declare tag 3 (IEEE_FLOAT), not 1, or decoders read the
    // bytes as integers; the extensible form carries the tag in SubFormat.
    v.setUint16(20, ext ? 0xFFFE : (isFloat ? 3 : 1), true);
    v.setUint16(22, numCh, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * ba, true);
    v.setUint16(32, ba, true); v.setUint16(34, bits, true);
    if (ext) {
        v.setUint16(36, 22, true);                       // cbSize
        v.setUint16(38, bits, true);                     // valid bits
        v.setUint32(40, WAV_CHANNEL_MASK[numCh] || 0, true);
        // SubFormat GUID xxxxxxxx-0000-0010-8000-00AA00389B71, first field = format tag.
        var g = [isFloat ? 3 : 1, 0, 0, 0, 0, 0, 0x10, 0, 0x80, 0, 0, 0xAA, 0, 0x38, 0x9B, 0x71];
        for (var i = 0; i < 16; i++) v.setUint8(44 + i, g[i]);
    }
    ws(headLen - 8, 'data'); v.setUint32(headLen - 4, dataLen, true);
    return ab;
}

/* __pcm16:begin
 * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
 * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
 * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
 * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
 * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
 * docs/why/pcm16-quantize.md.
 *
 * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
 * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
 *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
 *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
 *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
 *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
 *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
 *    точно — без шума и без округления мимо.
 *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
 *    уходит в шум, а не в тишину и не в гармоники).
 *  · dither=false — округление «половина к чётному», без шума: для того, что
 *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
 *  · Зерно постоянное: один и тот же вход — одни и те же байты.
 */
function pcm16Quantizer(dither) {
    var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
    // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
    // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
    // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
    // (32640…32767) ложно садился на сетку 32767 и терял единицу.
    function exact(src, b, e, dst, at, stride, pos) {
        for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
            var s = src[i];
            if (s !== s) s = 0;
            var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
            if (d > TOL || d < -TOL) return false;
            dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
        }
        return true;
    }
    return function (src, n, dst, at, stride) {
        // Состояние генератора — в локальной на время вызова: через замыкание
        // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
        var b, e, i, w, s, v, k, r = x, tri = dither;
        for (b = 0; b < n; b += BLOCK) {
            e = b + BLOCK < n ? b + BLOCK : n;
            if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
            for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                s = src[i];
                if (s !== s) s = 0;
                v = s < 0 ? s * 32768 : s * 32767;
                if (tri) {
                    r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                    k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                } else {
                    k = Math.floor(v + 0.5);
                    if (k - v === 0.5 && (k & 1)) k--;
                }
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
        }
        x = r;
    };
}
/* __pcm16:end */

function audioBufferToWav(buffer, bits, opts){
    bits = (bits === 24 || bits === 32) ? bits : 16;
    if (bits !== 16) return audioBufferToWavHiRes(buffer, bits);
    var q = pcm16Quantizer(!!(opts && opts.dither));
    var numCh = buffer.numberOfChannels, sr = buffer.sampleRate;
    // 16-bit PCM samples go into plain Int16Arrays and the Blob is assembled
    // as [header, ...pieces] — the browser concatenates without a JS
    // copy. Was: one DataView.setInt16 call per sample over a single
    // (44+data) buffer — per-call bounds checks made serialization of a
    // multi-minute buffer a multi-second main-thread block (26M+ calls for
    // 150 s stereo; /midi bench 2026-07-16). Int16Array stores are JIT-
    // vectorized and WAV is little-endian like every platform we run on
    // (typed-array endianness); the interleave+quantize collapses into the
    // same single pass.
    var frames = buffer.length, read = pcmReader(buffer), parts = [null];
    for (var at = 0; at < frames; at += WAV_PIECE_FRAMES) {
        var n = Math.min(WAV_PIECE_FRAMES, frames - at), pcm = new Int16Array(n * numCh);
        for (var ch = 0; ch < numCh; ch++) q(read(ch, at, n), n, pcm, ch, numCh);
        parts.push(pcm.buffer);
    }
    parts[0] = wavHeader(numCh, sr, 16, false, frames * numCh * 2);
    return new Blob(parts, { type: 'audio/wav' });
}

// 24-bit PCM and 32-bit float WAV. Split from the 16-bit writer so the hot
// path above keeps its single-pass Int16Array store; these carry the extra
// cost (packed 3-byte samples / 2× payload) only when the export asks for it.
function audioBufferToWavHiRes(buffer, bits){
    var numCh = buffer.numberOfChannels, sr = buffer.sampleRate;
    var frames = buffer.length;
    var isFloat = bits === 32;
    var bytesPerSample = isFloat ? 4 : 3;
    var ba = numCh * bytesPerSample;
    var dl = frames * numCh * bytesPerSample;
    var read = pcmReader(buffer), parts = [null];

    for (var at = 0; at < frames; at += WAV_PIECE_FRAMES) {
        var n = Math.min(WAV_PIECE_FRAMES, frames - at), body = new ArrayBuffer(n * ba);
        if (isFloat) {
            var f32 = new Float32Array(body);
            for (var ch = 0; ch < numCh; ch++) {
                var d = read(ch, at, n);
                for (var i = 0, w = ch; i < n; i++, w += numCh) f32[w] = d[i];
            }
        } else {
            // 24-bit little-endian, three bytes per sample.
            var u8 = new Uint8Array(body);
            for (var c2 = 0; c2 < numCh; c2++) {
                var d2 = read(c2, at, n);
                for (var j = 0, o = c2 * 3; j < n; j++, o += ba) {
                    // Масштаб декодера 24 бит (Chrome, Firefox, ffmpeg — ×8388608
                    // для обоих знаков) и округление: отрезание `| 0` с ×8388607
                    // у плюса возвращало нетронутый 24-битный файл бит-в-бит лишь
                    // на 50 % со смещением −0,5 бита (замер 28.10).
                    var v24 = Math.round(d2[j] * 8388608);
                    if (v24 > 8388607) v24 = 8388607; else if (!(v24 >= -8388608)) v24 = v24 < 0 ? -8388608 : 0;
                    u8[o]     =  v24        & 0xFF;
                    u8[o + 1] = (v24 >> 8)  & 0xFF;
                    u8[o + 2] = (v24 >> 16) & 0xFF;
                }
            }
        }
        parts.push(body);
    }
    parts[0] = wavHeader(numCh, sr, bits, isFloat, dl);
    return new Blob(parts, { type: 'audio/wav' });
}

/**
 * Decode a file WITHOUT the browser resampling it to the sound card's rate.
 *
 * `decodeAudioData` always resamples to the sampleRate of the context it runs
 * on, and a plain `new AudioContext()` inherits the OUTPUT DEVICE rate. So a
 * 48 kHz file opened on a 44.1 kHz card silently became 44.1 kHz — invisible
 * on a 48 kHz dev machine, which is why it went unnoticed (bug report
 * 2026-07-30: 24/48 in, 16/44.1 out).
 *
 * Fix: read the true rate from the file HEADER (cheap, no decode) and decode
 * inside an OfflineAudioContext pinned to it. Playback is unaffected — a
 * source-rate buffer played through a differently-clocked context is resampled
 * by the browser at output time (measured: 48k buffer in a 44.1k context keeps
 * its 4.000 s length and its 1000 Hz tone, with nothing at the 1088 Hz that a
 * naive replay would produce).
 *
 * @param {File|Blob} file
 * @param {ArrayBuffer} bytes decoded-from bytes (consumed; pass a copy if reused)
 * @param {AudioContext} fallbackCtx context to decode on when the rate is unknown
 * @returns {Promise<AudioBuffer>}
 */
async function decodeAtSourceRate(file, bytes, fallbackCtx){
    var srcRate = 0;
    try {
        if (window.AudioHeaderProbe && file) {
            var info = await window.AudioHeaderProbe.probe(file);
            if (info && info.sampleRate > 0) srcRate = info.sampleRate;
        }
    } catch (_) { /* probe is best-effort — never block the decode */ }

    // Unknown rate (ogg/m4a/… the header probe doesn't parse) or already equal:
    // decode the way the tool always did.
    if (!srcRate || (fallbackCtx && srcRate === fallbackCtx.sampleRate)) {
        return await fallbackCtx.decodeAudioData(bytes);
    }
    try {
        var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        var probeCtx = new OfflineCtx(1, 1, srcRate);
        return await probeCtx.decodeAudioData(bytes);
    } catch (e) {
        // Exotic rate the platform refuses — better the old behaviour than none.
        return await fallbackCtx.decodeAudioData(bytes);
    }
}

// ============================================================
// stretch — pitch-shift and/or time-stretch an AudioBuffer
// ============================================================

// Legacy FFmpeg asetrate+atempo fallback (WSOLA, mediocre for music).
// Used when engine='legacy' or when signalsmith refuses to run.
async function stretchViaFFmpeg(buffer, opts, slug){
    var sr = buffer.sampleRate;
    var semitones = opts.semitones || 0;
    var rate = opts.rate || 1;
    // FFmpeg filter: asetrate changes pitch+speed together, then atempo compensates speed back.
    // We combine the two factors: pitchRate from semitones, plus extra rate.
    var pitchRate = Math.pow(2, semitones / 12);
    var newSr = Math.round(sr * pitchRate);
    var tempoFactor = rate / pitchRate; // final speed = rate, pitch shifted by semitones

    // Build atempo chain (atempo's range is [0.5..2.0], so chain for wider factors)
    function buildAtempoChain(f){
        if (Math.abs(f - 1) < 0.0001) return '';
        var parts = [];
        while (f > 2.0) { parts.push('atempo=2.0'); f /= 2.0; }
        while (f < 0.5) { parts.push('atempo=0.5'); f *= 2.0; }
        parts.push('atempo=' + f.toFixed(8));
        return parts.join(',');
    }

    var filter = 'asetrate=' + newSr;
    var chain = buildAtempoChain(tempoFactor);
    if (chain) filter += ',' + chain;
    // Wrap with apad (1s leading+trailing silence) and atrim to expected
    // output duration. atempo otherwise drops the trailing partial frame
    // at frame boundaries — 10-18% truncation on sub-second inputs verified
    // on Herd 2026-06-09. Pre-pad lets atempo flush real content; post-trim
    // produces byte-exact output length regardless of input duration.
    var expectedOutDurSec = buffer.length / sr / rate;
    filter = 'apad=pad_dur=1,' + filter + ',atrim=duration=' + expectedOutDurSec.toFixed(6);

    var wav = audioBufferToWav(buffer);
    var hasReal = await ensureFFmpegProcessorScript(slug);
    if (!hasReal) throw new Error('FFmpeg engine unavailable');
    var ok = await window.FFmpegProcessor.init();
    if (!ok) throw new Error('FFmpeg init failed');
    var args = ['-af', filter, '-ar', String(sr), '-f', 'wav'];
    var signal = opts && opts.signal;
    var result = signal && window.FFmpegProcessor.processWithSignal
        ? await window.FFmpegProcessor.processWithSignal(signal, wav, args, 'out.wav', null, 'input.wav')
        : await window.FFmpegProcessor.process(wav, args, 'out.wav', 'input.wav');

    // Decode the result back to an AudioBuffer AT THE SOURCE RATE. A plain
    // `new AudioContext()` decodes at the sound card's rate, so a 44.1 kHz
    // stretch on a 48 kHz machine came back resampled with a different length
    // than the caller computed (decodeAtSourceRate above — same trap, same cure).
    var bytes = await result.blob.arrayBuffer();
    var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    try {
        return await new OfflineCtx(1, 1, sr).decodeAudioData(bytes.slice(0));
    } catch (_) {
        var ctx = new (window.AudioContext || window.webkitAudioContext)();
        try { return await ctx.decodeAudioData(bytes); }
        finally { try { ctx.close(); } catch(_){} }
    }
}

/* ── Which engine stretches TIME (tempo) without changing pitch ─────────────
   There are two families, and each is wrong for the other's material —
   measured 30.09.2026 on 20 speech clips (12 recordings + 8 neural TTS voices)
   and 21 songs, rates 0.5–3, after the /speed complaint «в превью превосходное,
   при сохранении ужасное» (Premium chat 661):
   · phase vocoder (Signalsmith): keeps each harmonic's magnitude, not their
     phase alignment — a voice's glottal pulses disperse (LPC-residual kurtosis
     7.5 → 3.3, i.e. noise-like) and it sounds hollow and metallic. UTMOS
     naturalness vs the original: −0.86 at 1.2×, −1.01 at 1.3×, −1.80 at 0.5×.
     On music it is the better one: Audiobox PQ −0.17 against WSOLA's −0.35
     at 1.2×, better on 15 of 21 songs (WSOLA flutters on sustained notes).
   · WSOLA (FFmpeg atempo, and what Chromium's own playbackRate does): copies
     waveform segments, so the voice keeps its pulses and attacks — UTMOS
     −0.11 at 1.2×, better than the vocoder on 19/20 clips at 1.2× and on
     every clip at 0.5×, 2× and 3×.
   So a pure tempo change routes by CONTENT: music → vocoder, anything else →
   WSOLA. The asymmetry decides the default: speech on the vocoder costs ~1
   MOS point, music on WSOLA ~0.2 PQ — an unknown verdict goes to WSOLA.

   ── …and which one shifts PITCH (measured the same day, 20 voices × −4…+7
   semitones, UTMOS vs the original; 21 songs, Audiobox PQ) ────────────────
   · speech, timbre kept (preserveFormants) → PSOLA (psola.js): −0.41. The
     vocoder with formant compensation: −1.49. Speech under a music bed
     (−10…−30 dB) or in noise: PSOLA still beats the vocoder on UTMOS and PQ.
   · speech, timbre moves with the pitch (formants off — «chipmunk») → tape +
     WSOLA (FFmpeg asetrate + atempo, stretchViaFFmpeg): −0.61 against the
     vocoder's −1.18; same character, no phasiness.
   · music → the vocoder: PQ −0.19 against tape + WSOLA −0.30 and PSOLA
     −0.14…−0.39 (a song is not one voice).
   Here the prior is music (/pitch is songs), so an UNKNOWN verdict stays on
   the vocoder; only a confident 'speech' (contentOf) leaves it. Warp curves
   and explicit formant shifts stay on the vocoder (it alone does them). */
// preserveFormants alone does not make it a pitch job: formant compensation
// only acts on a transposition, and callers pass it out of habit (the dubbing
// fit sends it with semitones 0).
function isPureTempo(opts, warp){
    return !warp
        && Math.abs(Number(opts.semitones) || 0) < 0.001
        && !(Number(opts.formantSemitones) || 0)
        && Math.abs((Number(opts.rate) || 1) - 1) >= 0.001;
}
function isPlainPitch(opts, warp){
    return !warp
        && Math.abs(Number(opts.semitones) || 0) >= 0.001
        && !(Number(opts.formantSemitones) || 0);
}
/* opts.interactive — the caller re-renders a whole song while a person waits to
   hear it (/karaoke: every key or tempo change). There the vocoder's quality
   edge on music (PQ −0.19 vs −0.30) costs 2.6× the wait: a 4-min song took
   11.8 s per key change against 4.5 s on FFmpeg (30.09.2026, same machine).
   Such a caller trades the 0.1 PQ for the wait, explicitly — music then rides
   tape + WSOLA / WSOLA. Exports (video-speed, reels-maker) do not pass it. */
function stretchEngineFor(opts, content){
    opts = opts || {};
    var warp = warpOf(opts);
    if (isPureTempo(opts, warp)) return content === 'music' && !opts.interactive ? 'phase-vocoder' : 'wsola';
    if (isPlainPitch(opts, warp)) {
        if (content === 'speech') return opts.preserveFormants ? 'psola' : 'tape-wsola';
        if (opts.interactive && !opts.preserveFormants) return 'tape-wsola';
    }
    return 'phase-vocoder';
}

/* The vocoder travels with the door. A page that includes audio-pipeline.js by
   its own <script> tag (karaoke, video-speed, reels-maker…) never runs the
   layout's ensureAudioPipeline(), which is what loads signalsmith-engine.js —
   so every stretch there went down to the last engine, FFmpeg, whatever the
   material: /karaoke's 1 106 key/tempo renders in 30 days were ALL 'ffmpeg'
   (tape + WSOLA; on songs Audiobox PQ −0.30 against the vocoder's −0.19, and
   a 30 MB core download instead of ~1 MB). */
var _vocoderLoad = null;
function ensureVocoder(slug){
    if (window.SignalsmithWorkerEngine) return Promise.resolve(true);
    if (_vocoderLoad) return _vocoderLoad;
    if (typeof window.__loadScriptsOnce !== 'function' || typeof window.__jsSrc !== 'function') return Promise.resolve(false);
    var t0 = Date.now();
    _vocoderLoad = window.__loadScriptsOnce([window.__jsSrc('/js/worker-watchdog.js'), window.__jsSrc('/js/warp-map.js'), window.__jsSrc('/js/signalsmith-engine.js')], 'stretch-vocoder')
        .then(function(){
            var ok = !!window.SignalsmithWorkerEngine;
            report(slug, 'pipeline_vocoder_lazy_load', { ok: ok ? 1 : 0, ms: Date.now() - t0 });
            return ok;
        })
        .catch(function(){ _vocoderLoad = null; report(slug, 'pipeline_vocoder_lazy_load', { ok: 0, ms: Date.now() - t0 }); return false; });
    return _vocoderLoad;
}

var _psolaLoad = null;
function ensurePsola(){
    if (window.Psola && typeof window.Psola.shiftBuffer === 'function') return Promise.resolve(true);
    if (_psolaLoad) return _psolaLoad;
    if (typeof window.__loadScriptsOnce !== 'function' || typeof window.__jsSrc !== 'function') return Promise.resolve(false);
    _psolaLoad = window.__loadScriptsOnce([window.__jsSrc('/js/psola.js')], 'psola')
        .then(function(){ return !!(window.Psola && window.Psola.shiftBuffer); })
        .catch(function(){ _psolaLoad = null; return false; });
    return _psolaLoad;
}

var _contentDetectLoad = null;
function ensureContentDetect(){
    if (window.ContentDetect && typeof window.ContentDetect.analyze === 'function') return Promise.resolve(true);
    if (_contentDetectLoad) return _contentDetectLoad;
    if (typeof window.__loadScriptsOnce !== 'function' || typeof window.__jsSrc !== 'function') return Promise.resolve(false);
    _contentDetectLoad = window.__loadScriptsOnce([window.__jsSrc('/js/content-detect.js')], 'content-detect')
        .then(function(){ return !!(window.ContentDetect && window.ContentDetect.analyze); })
        .catch(function(){ _contentDetectLoad = null; return false; });
    return _contentDetectLoad;
}

// 'speech' | 'music' | null — the caller's word first (it knows what a TTS
// voice is), otherwise the site's calibrated classifier with its YAMNet second
// opinion (fail-open: no config → the hand verdict).
// 'speech' only on EVIDENCE: the classifier also answers 'speech' when it could
// not look (its catch-all — silentRatio null) and inside its uncertain band
// without the second opinion (score 0.20–0.45, where 7 of 67 songs lived). For
// a tempo change null routes like speech anyway; for a pitch shift it must not
// send a song to PSOLA.
// One owner of that rule: a page that already ran ContentDetect (/video-audio,
// /denoise…) turns its result into the door's word here, not with a copy.
function contentFromDetect(base){
    if (!base) return null;
    if (base.content === 'music') return 'music';
    if (base.content === 'speech' && base.silentRatio != null && (base.musicScore < 0.20 || base.deep)) return 'speech';
    return null;
}
// opts.content: 'speech' | 'music' — the caller knows; 'unknown' — the caller
// LOOKED and does not know (no second classification of the same file).
async function contentOf(buffer, opts, slug){
    if (opts.content === 'speech' || opts.content === 'music') return opts.content;
    if (opts.content === 'unknown') return null;
    if (!(await ensureContentDetect())) return null;
    try {
        var base = window.ContentDetect.analyze(buffer);
        if (typeof window.ContentDetect.refine === 'function') base = await window.ContentDetect.refine(buffer, base);
        return contentFromDetect(base);
    } catch (e) {
        report(slug, 'pipeline_content_detect_failed', { err: ((e && e.message) || '').substring(0, 120) });
        return null;
    }
}

// Throw AbortError if opts.signal has been aborted. Cheap to call between any
// pipeline stages. Engines that accept .signal themselves get the same handle
// forwarded; engines that don't get checked between calls.
function _abortIfSignaled(signal){
    if (signal && signal.aborted) {
        var msg = (typeof signal.reason === 'string' && signal.reason) || 'AudioPipeline aborted';
        throw new DOMException(msg, 'AbortError');
    }
}

/* Кривая натяжения (opts.warp) — пары «время входа → время выхода», см.
   warp-map.js. Её исполняет ТОЛЬКО воркер Signalsmith: запасные движки
   (AudioWorklet PitchEngine, FFmpeg atempo) умеют один коэффициент на весь файл
   и молча выдали бы равномерное растяжение под видом выровненного — ровно тот
   класс, из-за которого /time-stretch отдавал «плывущий» трек как «134 BPM»
   (жалоба 24.09.2026). Поэтому с кривой запасных движков в цепочке нет, а отказ
   бросается типизированно (code WARP_UNAVAILABLE) — вызывающий показывает
   причину, а не файл, который ничего не исправил. */
function warpOf(opts){
    var w = opts && opts.warp;
    return (Array.isArray(w) && w.length >= 2) ? w : null;
}
function warpRate(w){
    var n = w.length;
    var dOut = w[n - 1][1] - w[0][1];
    return dOut > 0 ? (w[n - 1][0] - w[0][0]) / dOut : 1;
}
function warpUnavailable(reason, cause){
    var e = new Error('warp_unavailable: ' + reason + (cause ? ' (' + cause + ')' : ''));
    e.code = 'WARP_UNAVAILABLE';
    e.reason = reason;
    return e;
}

async function stretch(buffer, opts){
    opts = opts || {};
    var slug = opts.slug || null;
    var preferred = cfg('pitch_shift', 'signalsmith-worker');
    var signal = opts.signal || null;
    var warp = warpOf(opts);
    _abortIfSignaled(signal);

    // Memory guard — refuse early if the predicted peak would OOM the device. Caller can
    // catch this and surface a clear message instead of the cryptic "Array buffer
    // allocation failed" that leaks from OfflineAudioContext / audioBufferToWav.
    // С кривой длина выхода задаётся ею, а не opts.rate — считаем по её среднему.
    var effRate = warp ? warpRate(warp) : opts.rate;
    if (!stretchIsSafe(buffer, { rate: effRate })) {
        var peakMB = Math.round(estimateStretchPeakBytes(buffer, effRate) / 1048576);
        var capMB  = Math.round(deviceMemoryCapBytes() / 1048576);
        report(slug, 'pipeline_stretch_memory_guard', { peakMB: peakMB, capMB: capMB, durationSec: buffer.duration, channels: buffer.numberOfChannels });
        // Use a typed error so callers can do `catch (e) { if (ToolErrors.handle(e)) return; }`
        // and the global __handleError routes it as tool_blocked, not tool_error.
        var ErrClass = (window.ToolErrors && window.ToolErrors.FileTooLargeError) || Error;
        var fakeFile = { size: peakMB * 1048576, name: 'audio-buffer' };
        var err = new ErrClass(fakeFile, capMB * 1048576);
        err.message = 'Audio too large for in-browser processing (' + peakMB + ' MB predicted peak, device cap ' + capMB + ' MB). Try a shorter clip or close other tabs.';
        err.code = 'AUDIO_TOO_LARGE';
        err.reason = 'memory_guard';
        err.predictedMB = peakMB;
        err.capMB = capMB;
        throw err;
    }

    // Engine fallback chain: preferred → signalsmith → ffmpeg.
    // Each step records the engine via telemetry so a per-tool dashboard can
    // see how often the upgrade engine is actually serving traffic vs falling
    // back. RubberBand is the new default — best quality on music — but
    // signalsmith handles the "rubberband WASM blocked" case (corporate
    // proxies, ancient Android, content-blocker AV that scrubs WASM).
    var stretchOpts = {
        semitones: opts.semitones || 0,
        rate: opts.rate || 1,
        preserveFormants: !!opts.preserveFormants,
        formantSemitones: opts.formantSemitones || 0,
        transientsMode: opts.transientsMode || null,
        detectorMode: opts.detectorMode || null,
        onProgress: opts.onProgress || null,
        slug: slug
    };

    // Telemetry payload for the *_ok event. Captures the parameters that
    // shape output quality so we can answer questions like "are users on
    // detectorMode='percussive' actually invoked?" or "what fraction of
    // sessions are speedups (rate>1) vs slowdowns (rate<1)?".
    if (warp) {
        stretchOpts.warp = warp;
        stretchOpts.lane = opts.lane;
    }
    var okMeta = {
        semitones: Math.round(stretchOpts.semitones * 10) / 10,
        rate: Math.round((warp ? effRate : stretchOpts.rate) * 1000) / 1000,
        formant: stretchOpts.preserveFormants ? 1 : 0,
        transients: stretchOpts.transientsMode || 'crisp',
        detector: stretchOpts.detectorMode || 'compound',
        contentType: opts.contentType || null,
        onsetDensity: typeof opts.onsetDensity === 'number' ? Math.round(opts.onsetDensity * 100) / 100 : null
    };
    if (warp) okMeta.warp = warp.length;

    // С кривой — один исполнитель, без запасных (см. warpOf выше).
    if (warp) {
        if (preferred !== 'legacy') await ensureVocoder(slug);
        if (preferred === 'legacy' || !window.SignalsmithWorkerEngine) {
            report(slug, 'pipeline_stretch_failed', { engine: 'signalsmith-worker', warp: warp.length, err: 'engine_unavailable' });
            throw warpUnavailable('engine_unavailable');
        }
        if (!window.WarpMap) {
            report(slug, 'pipeline_stretch_failed', { engine: 'signalsmith-worker', warp: warp.length, err: 'warp_module_missing' });
            throw warpUnavailable('warp_module_missing');
        }
        stretchOpts.signal = signal;
        try {
            var warped = await window.SignalsmithWorkerEngine.shift(buffer, stretchOpts);
            okMeta.engine = 'signalsmith-worker';
            report(slug, 'pipeline_stretch_ok', okMeta);
            return warped;
        } catch (werr) {
            if (werr && werr.name === 'AbortError') throw werr;
            report(slug, 'pipeline_stretch_failed', { engine: 'signalsmith-worker', warp: warp.length, err: ((werr && werr.message) || '').substring(0, 120) });
            throw warpUnavailable('engine_failed', werr && werr.message);
        }
    }

    // Forward the signal to engines that accept it. None of the sub-engines
    // currently honour opts.signal, so the abort check between stages is the
    // mechanism — cancel between rubberband→signalsmith→ffmpeg fallbacks is
    // observed, mid-engine cancel is not. Pragmatic: stretches are typically
    // short (≤ a few seconds per engine attempt) so inter-stage checks cover
    // most real cancel UX.
    stretchOpts.signal = signal;

    // === A tempo change picks its engine by what is being stretched ===
    // (stretchEngineFor above: speech keeps its voice only on WSOLA). If WSOLA
    // cannot run (FFmpeg blocked or out of memory) the vocoder still delivers
    // — a file with its flaw beats no file — and the fallback is reported.
    if (isPureTempo(opts, null)) {
        var tempoContent = await contentOf(buffer, opts, slug);
        okMeta.content = tempoContent || 'unknown';
        _abortIfSignaled(signal);
        if (stretchEngineFor(opts, tempoContent) === 'wsola') {
            // WSOLA runs in FFmpeg, which a page like /text-to-speech loads on
            // first use (~30 MB): `ms` shows what that costs real people.
            var wsolaT0 = Date.now();
            try {
                var wsolaOut = await stretchViaFFmpeg(buffer, opts, slug);
                okMeta.engine = 'wsola';
                okMeta.ms = Date.now() - wsolaT0;
                okMeta.dur = Math.round(buffer.duration * 10) / 10;
                report(slug, 'pipeline_stretch_ok', okMeta);
                return wsolaOut;
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                report(slug, 'pipeline_stretch_fallback', { engine: 'wsola', content: okMeta.content, err: ((err && err.message) || '').substring(0, 120) });
            }
        }
    }

    // === A pitch shift picks its engine by content and by the timbre asked for ===
    // (stretchEngineFor above). Speech leaves the vocoder: PSOLA when the voice's
    // timbre is kept (a tempo change on top goes through WSOLA as a second pass —
    // both keep the glottal pulses), tape + WSOLA when the timbre moves with the
    // pitch. A failure reports and falls through to the vocoder: a file with its
    // flaw beats no file.
    if (isPlainPitch(opts, null)) {
        var pitchContent = await contentOf(buffer, opts, slug);
        okMeta.content = pitchContent || 'unknown';
        _abortIfSignaled(signal);
        var pitchEngine = stretchEngineFor(opts, pitchContent);
        if (pitchEngine === 'psola') {
            var psolaT0 = Date.now();
            try {
                if (!(await ensurePsola())) throw new Error('psola module unavailable');
                var psolaStats = null;
                var rateNow = Number(opts.rate) || 1;
                var psolaOut = await window.Psola.shiftBuffer(buffer, stretchOpts.semitones, {
                    signal: signal,
                    onStats: function(s){ psolaStats = s; },
                    onProgress: stretchOpts.onProgress
                        ? function(p){ try { stretchOpts.onProgress(Math.abs(rateNow - 1) >= 0.001 ? 0.7 * p : p); } catch(_){} }
                        : null
                });
                if (Math.abs(rateNow - 1) >= 0.001) {
                    psolaOut = await stretchViaFFmpeg(psolaOut, { rate: rateNow, semitones: 0, signal: signal }, slug);
                }
                okMeta.engine = 'psola';
                okMeta.ms = Date.now() - psolaT0;
                okMeta.dur = Math.round(buffer.duration * 10) / 10;
                if (psolaStats) { okMeta.gainDb = psolaStats.gainDb; okMeta.voiced = psolaStats.voiced; if (psolaStats.ceiling) okMeta.ceilDb = psolaStats.ceiling.grDb; }
                report(slug, 'pipeline_stretch_ok', okMeta);
                return psolaOut;
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                report(slug, 'pipeline_stretch_fallback', { engine: 'psola', content: okMeta.content, err: ((err && err.message) || '').substring(0, 120) });
            }
        } else if (pitchEngine === 'tape-wsola') {
            var tapeT0 = Date.now();
            try {
                var tapeOut = await stretchViaFFmpeg(buffer, opts, slug);
                okMeta.engine = 'tape-wsola';
                okMeta.ms = Date.now() - tapeT0;
                okMeta.dur = Math.round(buffer.duration * 10) / 10;
                report(slug, 'pipeline_stretch_ok', okMeta);
                return tapeOut;
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                report(slug, 'pipeline_stretch_fallback', { engine: 'tape-wsola', content: okMeta.content, err: ((err && err.message) || '').substring(0, 120) });
            }
        }
    }

    // === Short-buffer routing ===
    // ⚠️ THE DIAGNOSIS BELOW WAS WRONG, and the artefact it describes is fixed.
    // The 2026-06-09 observation was real — 0.1s sine came back with ~50ms of
    // leading silence — but the conclusion ("non-linear, engine-state-dependent,
    // no pad-then-trim at this layer can fix it") was not. It was ONE
    // uncompensated term: signalsmith-worker.js stripped outputLatency and left
    // inputLatency in. Measured 2026-09-01 with a click at a known time: a
    // CONSTANT +2646 samples (60 ms), identical at -3/-7/+5/+12 semitones and
    // exactly inputLatency/rate at rate=2 — linear, and fixed at the source
    // (c5ada4087a). Re-measured after: a 0.1 s input now returns with zero
    // leading silence and full amplitude.
    //
    // So this route is kept for now but is NO LONGER justified by the reason
    // written here. Removing it is a real simplification — it would put
    // sub-second inputs back on signalsmith instead of FFmpeg — but that swaps
    // the engine serving those clips, so it needs its own quality measurement
    // rather than a drive-by deletion. Do not repeat the original mistake of
    // building on an unverified explanation.
    //
    // FFmpeg's atempo, by contrast, has a deterministic frame-boundary
    // behaviour that we close cleanly by wrapping the filter chain with
    // apad+atrim inside stretchViaFFmpeg (above). For short inputs we
    // bypass the signalsmith chain entirely and route to the FFmpeg
    // engine, which is byte-exact and silence-free for every duration.
    //
    // 1.0s threshold: signalsmith engine warmup leaks ~50-100ms of silence
    // at output start; for >1s inputs this is <5% of total and below user-
    // perceptual threshold. Below 1s the artefact dominates content.
    var inputDurSec = buffer.duration;
    if (inputDurSec < 1.0) {
        okMeta.shortBufferRoute = 1;
        try {
            var ffOut = await stretchViaFFmpeg(buffer, opts, slug);
            okMeta.engine = 'ffmpeg';
            report(slug, 'pipeline_stretch_ok', okMeta);
            return ffOut;
        } catch (err) {
            if (err && err.name === 'AbortError') throw err;
            report(slug, 'pipeline_stretch_fallback', { engine: 'ffmpeg', err: (err.message || '').substring(0, 120), reason: 'short_input' });
            // Fall through to signalsmith chain as a last resort — output
            // may have silence padding but at least the user gets a file.
        }
    }

    if (preferred !== 'legacy' && !window.SignalsmithWorkerEngine) {
        await ensureVocoder(slug);
        _abortIfSignaled(signal);
    }
    if (preferred !== 'legacy' && window.SignalsmithWorkerEngine) {
        try {
            var shifted = await window.SignalsmithWorkerEngine.shift(buffer, stretchOpts);
            okMeta.engine = 'signalsmith-worker';
            report(slug, 'pipeline_stretch_ok', okMeta);
            return shifted;
        } catch(err) {
            if (err && err.name === 'AbortError') throw err;
            console.warn('[audio-pipeline] rubberband stretch failed, falling back to signalsmith:', err.message);
            report(slug, 'pipeline_stretch_fallback', { engine: 'signalsmith-worker', err: (err.message || '').substring(0, 120) });
            // Fall through to signalsmith
        }
    }

    _abortIfSignaled(signal);
    if (preferred !== 'legacy' && window.PitchEngine) {
        try {
            var shifted2 = await window.PitchEngine.shift(buffer, stretchOpts);
            okMeta.engine = 'signalsmith';
            report(slug, 'pipeline_stretch_ok', okMeta);
            return shifted2;
        } catch(err) {
            if (err && err.name === 'AbortError') throw err;
            console.warn('[audio-pipeline] signalsmith stretch failed, falling back to ffmpeg:', err.message);
            report(slug, 'pipeline_stretch_fallback', { engine: 'signalsmith', err: (err.message || '').substring(0, 120) });
            // Fall through to FFmpeg
        }
    }

    _abortIfSignaled(signal);
    // Legacy path (or explicit kill-switch)
    try {
        var out = await stretchViaFFmpeg(buffer, opts, slug);
        okMeta.engine = 'ffmpeg';
        report(slug, 'pipeline_stretch_ok', okMeta);
        return out;
    } catch(err) {
        if (err && err.name === 'AbortError') {
            report(slug, 'pipeline_stretch_cancelled', { engine: 'ffmpeg' });
            throw err;
        }
        report(slug, 'pipeline_stretch_failed', { engine: 'ffmpeg', err: (err.message || '').substring(0, 120) });
        throw err;
    }
}

// ============================================================
// encodeMP3 — AudioBuffer/WAV → MP3 Blob
// ============================================================

// FFmpeg MP3 encode. The bare FFmpeg path historically lacked the two
// guarantees the lamejs worker path already has, which is why a force-route to
// FFmpeg (the WAV-blob mis-estimate this file's estimateMP3PeakBytes comment
// describes) surfaced as "stuck on Обработка":
//   (A) REAL progress — every FFmpeg exec dispatches a global `ffmpeg-progress`
//       window event (0..1); we forward it to onProgress so the caller's bar
//       moves instead of freezing. (Mirrors FFmpegProcessor.execGuarded's
//       progressForwarder.)
//   (B) BOUNDED failure — a stall watchdog aborts a wedged single-thread exec
//       after a generous no-progress window, the same contract lamejs has via
//       its 30 s heartbeat (mp3-encoder.js → WORKER_HUNG). Without it a
//       deadlocked exec (ffmpeg.wasm native timeout = -1) never resolves nor
//       rejects → the button stays on "Обработка" forever with no reset.
// lane.channelArgs === false — the warm lane (warmFfmpegMp3Lane): the buffer's own layout, as
// lamejs would encode it, not the page's channel picker (which lamejs never read either).
async function encodeMP3ViaFFmpeg(wavBlob, bitrate, slug, vbrQuality, signal, onProgress, paid, lane){
    var hasReal = await ensureFFmpegProcessorScript(slug);
    if (!hasReal) throw new Error('FFmpeg engine unavailable');
    var ok = await window.FFmpegProcessor.init();
    if (!ok) throw new Error('FFmpeg init failed');
    var args;
    if (typeof vbrQuality === 'number') {
        // VBR via libmp3lame -q:a (0=best, 9=worst). Matches lame --vbr-new -V <q>.
        args = ['-c:a', 'libmp3lame', '-q:a', String(Math.max(0, Math.min(9, vbrQuality)))];
    } else {
        args = window.FFmpegProcessor.getCodecArgs('mp3', bitrate, { paid: paid === true });
    }
    /* Вторая дверь этого лейна — граница exec: она зажимает любые рукописные
       -b:a по расширению выхода и про вызов ничего не знает.
       ⚠️ Метка едет ЗНАЧЕНИЕМ, а не свойством массива. Замерено 18.09.2026:
       свойство переживает FFmpegDelayCompensation.apply (тот же массив), но
       FFmpegResampleQuality.apply делает concat — новый массив, свойство
       потеряно, и платная выдача снова приходила 129810 bps вместо 192к.
       Значение переживает concat/slice/map, то есть любую перестройку
       аргументов. Границу оно НЕ переживает: она его вырезает. Если её
       когда-нибудь уберут, ffmpeg упадёт на неизвестной опции ГРОМКО, и
       платный MP3 уйдёт в WAV-фолбэк — это лучше, чем снова молча отдать
       128. */
    if (paid === true) args = [PAID_ARG, '1'].concat(args);
    if (window.ChannelModule && window.ChannelModule.getArgs && !(lane && lane.channelArgs === false)) args = args.concat(window.ChannelModule.getArgs());

    function nowMs(){ return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

    // (A) progress forwarder + (B) stall-clock tick. The WAV MEMFS mount runs
    // BEFORE the first progress event and was observed up to ~110 s on low-RAM
    // mobiles, so the stall window must clear that — 180 s no-progress is well
    // above the worst real mount yet still converts an infinite hang into a
    // bounded WAV fallback. Once exec emits progress the clock resets, so a
    // slow-but-alive encode is never false-aborted.
    var STALL_MS = 180000;
    var lastTick = nowMs();
    var settled = false;
    var progressForwarder = function(ev){
        lastTick = nowMs();
        var p = ev && ev.detail;
        if (typeof p === 'number' && typeof onProgress === 'function') { try { onProgress(p); } catch(_){} }
    };
    try { window.addEventListener('ffmpeg-progress', progressForwarder); } catch(_){}

    // Compose the caller's cancel signal (if any) with the stall watchdog so a
    // user Cancel and a watchdog stall both terminate the same exec via the
    // existing processWithSignal → FFmpegProcessor.abort() bridge.
    var stallCtl = (typeof AbortController === 'function') ? new AbortController() : null;
    var effSignal = signal || (stallCtl && stallCtl.signal) || null;
    if (signal && stallCtl) {
        if (signal.aborted) { try { stallCtl.abort(signal.reason); } catch(_){} }
        else { try { signal.addEventListener('abort', function(){ try { stallCtl.abort(signal.reason); } catch(_){} }, { once: true }); } catch(_){} }
        effSignal = stallCtl.signal;
    }
    var stallTimer = stallCtl ? setInterval(function(){
        if (settled) return;
        if (nowMs() - lastTick > STALL_MS) {
            report(slug, 'pipeline_mp3_ffmpeg_stalled', { sinceMs: Math.round(nowMs() - lastTick) });
            try { stallCtl.abort('ffmpeg_stalled'); } catch(_){}
        }
    }, 5000) : null;

    try {
        var result = effSignal && window.FFmpegProcessor.processWithSignal
            ? await window.FFmpegProcessor.processWithSignal(effSignal, wavBlob, args, 'out.mp3', null, 'input.wav')
            : await window.FFmpegProcessor.process(wavBlob, args, 'out.mp3', 'input.wav');
        return result.blob;
    } finally {
        settled = true;
        if (stallTimer) clearInterval(stallTimer);
        try { window.removeEventListener('ffmpeg-progress', progressForwarder); } catch(_){}
    }
}

// Device RAM budget for in-memory audio work: 25% of window.__memBudget.gb(), which leaves room
// for the JS heap, the browser's decode pipeline, and OS pressure on low-end devices. The gb comes
// from the ONE owner (layout, 09.10.2026): this used to read `navigator.deviceMemory || 2` itself,
// i.e. a 512 MB cap for desktop Firefox and Safari (no deviceMemory there) where the owner says
// 8 GB — and the warm-engine MP3 lane below asks this same budget.
function deviceMemoryCapBytes(){
    var gb = 2;
    try { var B = window.__memBudget; if (B && typeof B.gb === 'function') gb = B.gb(); } catch (_) {}
    return gb * 1073741824 * 0.25;
}

// Estimate peak PCM memory footprint for lamejs MP3 encode on main thread.
// We hold the AudioBuffer, copy its Float32 channels (~2× briefly during transfer), the
// worker allocates its own Float32 views then Int16 chunks. ~2× rule-of-thumb is sane.
//
// CRITICAL: branch on the blob's *container*, not a blanket compressed guess.
// The single biggest source of input here is an UNCOMPRESSED WAV blob produced
// by audioBufferToWav (pitch / vocal-remover / kalimba / rodent-repellent /
// reverb-echo / text-to-speech-Piper all do `encodeMP3(wavBlob)`). A WAV blob's
// bytes ARE the PCM, so its real lamejs peak equals the AudioBuffer-path formula
// below (length·ch·4·2). Applying the 128 kbps-compressed duration estimate to a
// WAV over-counted it by ~10× — a 90 MB WAV read as 3.9 GB — which force-tripped
// the memory guard and rerouted in-browser MP3 exports to the much slower
// single-threaded FFmpeg WASM path (telemetry 2026-06-05→08: ~200 misroutes/3.5d
// across 6 tools; FFmpeg MEMFS mount alone up to 110 s → users perceive a hang and
// abandon). The compressed floor is kept ONLY for genuinely compressed/unknown
// blobs, where over-estimating is harmless (routes to FFmpeg = safe-but-slow).
function estimateMP3PeakBytes(input){
    if (input instanceof Blob) {
        var t = (input.type || '').toLowerCase();
        // Uncompressed PCM containers — the bytes are (approximately) the PCM.
        // ×4 == the 16-bit-WAV → Float32-decode (×2) + lamejs working copy (×2),
        // i.e. identical to the AudioBuffer estimate for the same audio, so the
        // guard now treats `wavBlob(buffer)` and `buffer` consistently.
        var uncompressed = t.indexOf('wav') >= 0 || t.indexOf('wave') >= 0 ||
                           t.indexOf('aiff') >= 0 || t.indexOf('pcm') >= 0 ||
                           t.indexOf('l16') >= 0;
        if (uncompressed) return input.size * 4;
        // Compressed (mp3/ogg/opus/m4a/aac/webm/flac) or untyped — approximate
        // decoded size from a 128 kbps floor.
        var guessDurationSec = input.size / (128 * 1024 / 8);
        return guessDurationSec * 44100 * 2 * 4 * 2;
    }
    if (input && (typeof input.getChannelData === 'function' || typeof input.copyFromChannel === 'function')) {
        // The encoder knows what it holds: since 2026-10-01 it streams a buffer in pieces, and its
        // peak no longer grows with the length. Two whole copies was the old encode() — and the
        // estimate that sent long buffers to FFmpeg through a WHOLE WAV, which costs more.
        if (window.MP3Encoder && typeof window.MP3Encoder.peakBytes === 'function') return window.MP3Encoder.peakBytes(input);
        return input.length * input.numberOfChannels * 4 * 2;
    }
    return 0;
}

function lamejsIsSafe(input){
    var peakBytes = estimateMP3PeakBytes(input);
    return peakBytes <= 0 || peakBytes < deviceMemoryCapBytes();
}

// Peak memory estimate for a pitch/time-stretch operation.
// Must hold, concurrently, the input AudioBuffer + the OfflineAudioContext rendering target
// + post-render temporaries (audioBufferToWav Int16 copy = channels × length × 2, plus the
// transient Float32 → Int16 conversion). We use a 3× coefficient over the input PCM size.
function estimateStretchPeakBytes(buffer, rate){
    if (!buffer || typeof buffer.getChannelData !== 'function') return 0;
    var inBytes = buffer.length * buffer.numberOfChannels * 4;
    var rateFactor = rate && rate > 0 ? Math.max(1, 1 / rate) : 1; // slowing increases output size
    return Math.round(inBytes * (2 + rateFactor));
}

function stretchIsSafe(buffer, opts){
    var peakBytes = estimateStretchPeakBytes(buffer, opts && opts.rate);
    return peakBytes <= 0 || peakBytes < deviceMemoryCapBytes();
}

// Short-buffer edge protection — handled in stretch() by routing sub-second
// inputs to stretchViaFFmpeg, which wraps atempo with apad+atrim for byte-
// exact output. signalsmith chain is kept for ≥1s inputs where its warmup
// latency is <5% of total (perceptually inaudible).
// See stretch() short-buffer routing block + stretchViaFFmpeg's apad+atrim
// wrapper. Bug-report 2026-06-08 / Voice Oohs.wav root cause.

// ── window.__toolProgress on demand, for callers that never loaded it ──
// The panel's script is pulled in by <x-tool-progress>; a page that exports an
// MP3 without declaring that component still needs it. Versioned path comes
// from the layout (window.__TOOL_PROGRESS_SRC) — a runtime must not build
// `/js/...` by hand, static assets are served with a year-long max-age.
var _tprogPending = null;
function ensureToolProgress(){
    if (window.__toolProgress) return Promise.resolve(window.__toolProgress);
    if (_tprogPending) return _tprogPending;
    if (!window.__TOOL_PROGRESS_SRC) return Promise.reject(new Error('no-tprog-src'));
    _tprogPending = new Promise(function(res, rej){
        try {
            var sc = document.createElement('script');
            sc.src = window.__TOOL_PROGRESS_SRC;
            sc.async = true;
            var n = document.querySelector('script[nonce]');
            if (n && n.nonce) sc.setAttribute('nonce', n.nonce);
            sc.onload = function(){ window.__toolProgress ? res(window.__toolProgress) : rej(new Error('tprog-missing')); };
            sc.onerror = function(){ _tprogPending = null; rej(new Error('tprog-load-failed')); };
            document.head.appendChild(sc);
        } catch(e) { _tprogPending = null; rej(e); }
    });
    return _tprogPending;
}

/* ── Progress is the DEFAULT, not an opt-in (2026-08-31) ──────────────────
 *
 * The MP3 worker has emitted a percent every 5 % of the way since it was
 * written. Of the 35 call sites of this function across the site, ONE passed
 * an `onProgress` to receive it. The other 34 threw it away, so every long
 * export on this platform ran behind whatever static text the tool happened to
 * have set — /extend-song's was a progress bar pinned at 50 % for the whole
 * run. Measured on the reported case (a 10-minute track extended to 50): 195 s
 * here, ~450 s on the reporter's laptop, bar motionless throughout. He clicked
 * again (the tool's own busy latch swallowed it), rage-clicked, re-ran the
 * whole tool three times and wrote «Почему музыка не скачивается?».
 *
 * A tool cannot be relied on to ask for this — the same argument that put the
 * download budget in __downloadFile rather than in 181 call sites. So the
 * pipeline raises the shared panel itself when nobody else is going to.
 *
 * Deliberately quiet in the common case:
 *   · a caller with its own onProgress owns the surface — we do nothing;
 *   · nothing appears until the run has ALREADY taken longer than SHOW_AFTER,
 *     so the hundreds of short exports (a drum take, a TTS phrase) never flash
 *     a panel;
 *   · if some panel is already visible, we do not add a second answer to the
 *     same question;
 *   · opts.progress === false opts out entirely.
 */
var PROGRESS_SHOW_AFTER_MS = 2500;

function defaultProgress(opts){
    if (opts.onProgress || opts.progress === false) return null;
    if (typeof document === 'undefined' || !document.body) return null;
    var state = { panel: null, ratio: 0, dead: false, timer: 0 };
    state.timer = setTimeout(function(){
        if (state.dead) return;
        ensureToolProgress().then(function(tp){
            // Re-check both gates at mount time, not at scheduling time: the
            // run may have finished, or the tool may have raised its own panel,
            // in the seconds we waited.
            if (state.dead || !tp || tp.anyVisible()) return;
            var i18n = window.__TPROG_I18N || {};
            state.panel = tp.floating();
            state.panel.start(i18n.mp3 || 'MP3');
            state.panel.set(state.ratio);
        }).catch(function(){ /* no panel available — the run is unaffected */ });
    }, PROGRESS_SHOW_AFTER_MS);
    return {
        onProgress: function(p){ state.ratio = p; if (state.panel) state.panel.set(p); },
        end: function(){
            state.dead = true;
            clearTimeout(state.timer);
            if (state.panel) { try { state.panel.done(); state.panel.hide(); } catch(_){} state.panel = null; }
        }
    };
}

async function encodeMP3(input, opts){
    opts = opts || {};
    var _prog = defaultProgress(opts);
    if (_prog) opts = Object.assign({}, opts, { onProgress: _prog.onProgress });
    try {
        return await encodeMP3Inner(input, opts);
    } finally {
        if (_prog) _prog.end();
    }
}

/* ── How fast this actually is, and three levers that are NOT there ───────
 *
 * Measured 2026-08-31 on a 32-core desktop, 192 kbps CBR stereo 44.1 k, real
 * music-like PCM (silence and sparse buffers compress at speeds that mean
 * nothing — my first pass used one and "found" a 6x win that does not exist).
 *
 *   lamejs, one worker .......... 11.5–19x realtime.
 *                                 300 s of audio → 17 s; 3015 s → 195 s.
 *
 *   lamejs vs FFmpeg-WASM ....... EQUAL within 10 % at 30/120/300/900 s
 *                                 (1.6/7.1/17.3/47.7 s vs 1.7/7.1/16.3/52.6 s,
 *                                 each in a fresh context so the engine load is
 *                                 paid honestly). There is no faster lane to
 *                                 switch to; the guard above routes on MEMORY,
 *                                 and that is the only reason it should.
 *
 *   bigger encode BLOCK ......... 1.03–1.04x at 8x and 32x granules. Nothing.
 *
 *   N-way sharding .............. 3.43x at 4 shards (15.7 s → 4.6 s on 180 s),
 *                                 and REJECTED. The vendored lamejs runs with
 *                                 disable_reservoir=true, so frames are self
 *                                 contained and concatenation is structurally
 *                                 valid — but every extra lame instance
 *                                 contributes its own encoder delay, measured
 *                                 as +0.078 s over 3 joins, i.e. one 26 ms
 *                                 granule of ramp-in inserted at each. It
 *                                 cannot be trimmed away exactly: LAME's delay
 *                                 is 1105 samples and the frame grid is 1152,
 *                                 so dropping the priming frame still leaves a
 *                                 47-sample splice. On /extend-song, whose
 *                                 entire product is inaudible joins, buying
 *                                 3.4x with three audible ones is the wrong
 *                                 trade. Revisit only with a plan that is
 *                                 sample-exact, not with a faster concatenation.
 *
 * So the wait is real and it stays. What it must not be is invisible — hence
 * defaultProgress() above.
 *
 * ── …except when the engine is ALREADY loaded (09.10.2026) ────────────────
 *
 * The "EQUAL" row above paid FFmpeg's engine load on every run (a fresh
 * context each time). Most audio pages warm that engine the moment a file is
 * picked (layout __warmUpFFmpeg), so by the time a buffer reaches this
 * function the load is usually already paid — and what is left is the encode
 * itself, where libmp3lame (C in wasm) is several times faster than lamejs
 * (JS). Measured 09.10.2026 on the /speed page, warm engine, the two encoders
 * interleaved on the same decoded buffers (48 kHz stereo; the machine was
 * busy, so read the ratios, not the seconds):
 *     30 s   128k  libmp3lame 1.5–1.6 s   lamejs 5.7–6.2 s
 *     258 s  128k  libmp3lame 13.5–14.0   lamejs 41.7–46.8
 *     258 s  192k  libmp3lame 11.8–14.3   lamejs 42.7–45.4
 *     774 s  128k  libmp3lame 40.8–44.5   lamejs 117.5–118.8
 *     774 s  192k  libmp3lame 29.0–31.4   lamejs 91.3–104.6
 * i.e. ×3.1–3.6 at every length. On /speed
 * that encode is the longest step of the vocoder path, and the step people
 * gave up during: 01–08.10, MP3 through the vocoder → lamejs — 11 % of
 * exports over a minute and 5.4 % never finished; MP3 straight through
 * FFmpeg — 0.8 % (review #1822, «не скачивает просто весит»).
 *
 * So warmFfmpegMp3Lane() sends the buffer to the loaded engine when nothing
 * would come out differently: the engine is loaded AND idle (this function
 * never loads it — an unloaded engine is the fresh-context case, where the
 * measurement above stands), the WAV fits the same memory budget the rest of
 * this file uses (whole-WAV memory is exactly what the 01.10 piecewise lamejs
 * lane removed on 2 GB machines — those never warm the engine, and the
 * budget keeps long buffers off it anyway), the caller wants a plain CBR
 * file (no explicit ID3, no bare frames — the FFmpeg lane writes its own
 * container), and the rate and layout are ones lame takes as they are. Any
 * failure falls back to lamejs, exactly as before.
 */
var LAME_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];
function warmFfmpegMp3Lane(input, opts){
    if (opts.bare || opts.id3 || opts.id3v1) return false;
    if (!input || typeof input.numberOfChannels !== 'number' || !(input.length > 0)) return false;   // a WAV Blob: rate unknown
    if (input.numberOfChannels > 2 || LAME_RATES.indexOf(input.sampleRate) < 0) return false;
    var FP = window.FFmpegProcessor;
    if (!FP || typeof FP.isReady !== 'function' || !FP.isReady() || FP.__isStub) return false;
    if ((FP._inFlight || 0) > 0) return false;
    // The WAV Blob, the one contiguous read the engine copies in, the copy in its heap.
    var wavBytes = 44 + input.length * input.numberOfChannels * 2;
    return wavBytes * 3 <= deviceMemoryCapBytes();
}

async function encodeMP3Inner(input, opts){
    opts = opts || {};
    var bitrate = opts.bitrate || 192;
    var slug = opts.slug || null;
    var preferred = cfg('mp3_encoder', 'lamejs');
    var wantsVBR = typeof opts.vbrQuality === 'number';
    var signal = opts.signal || null;
    _abortIfSignaled(signal);

    var asWav = null;
    if (input instanceof Blob) asWav = input;

    // Route VBR requests to FFmpeg. The vendored lamejs (zhuker/lamejs) is CBR-only — its
    // Mp3Encoder constructor takes a fixed kbps. Pretending to support VBR with CBR output
    // would silently ship the wrong thing to users; FFmpeg's libmp3lame does real VBR.
    if (wantsVBR) {
        report(slug, 'pipeline_mp3_vbr_via_ffmpeg', { vbrQuality: opts.vbrQuality });
        if (!asWav) asWav = (input instanceof Blob) ? input : audioBufferToWav(input);
        try {
            var vbrBlob = await encodeMP3ViaFFmpeg(asWav, bitrate, slug, opts.vbrQuality, signal, opts.onProgress, opts.paid === true);
            report(slug, 'pipeline_mp3_ok', { engine: 'ffmpeg', mode: 'vbr', size: vbrBlob.size });
            return vbrBlob;
        } catch(err) {
            report(slug, 'pipeline_mp3_failed', { engine: 'ffmpeg', mode: 'vbr', err: (err.message || '').substring(0, 120) });
            throw err;
        }
    }

    // The engine already on the page (see warmFfmpegMp3Lane above).
    if (warmFfmpegMp3Lane(input, opts)) {
        var warmT0 = Date.now();
        try {
            var warmBlob = await encodeMP3ViaFFmpeg(audioBufferToWav(input), bitrate, slug, undefined, signal,
                opts.onProgress, opts.paid === true, { channelArgs: false });
            report(slug, 'pipeline_mp3_ok', { engine: 'ffmpeg', lane: 'warm', size: warmBlob.size,
                ms: Date.now() - warmT0, dur: Math.round(input.duration || (input.length / input.sampleRate)) });
            return warmBlob;
        } catch (err) {
            if (signal && signal.aborted) throw err;
            report(slug, 'pipeline_mp3_fallback', { engine: 'ffmpeg_warm', err: String((err && err.message) || err).substring(0, 120) });
            // lamejs below, as before
        }
    }

    // Safety first: for files that would blow out RAM on this device, skip lamejs entirely.
    // FFmpeg reads from its virtual filesystem in chunks, so memory stays roughly constant
    // regardless of input length — the right tool for hour-long files on mobile.
    var safe = lamejsIsSafe(input);
    if (preferred === 'lamejs' && !safe) {
        report(slug, 'pipeline_mp3_memory_guard', { reason: 'input_too_large_for_lamejs' });
    }

    _abortIfSignaled(signal);
    if (preferred === 'lamejs' && safe && window.MP3Encoder) {
        try {
            var blob = await window.MP3Encoder.encode(input, {
                bitrate: bitrate,
                slug: slug,
                vbrQuality: opts.vbrQuality,
                signal: signal,
                id3: opts.id3 || null,
                id3v1: opts.id3v1 || null,
                onProgress: opts.onProgress || null,
                // Выдача, купленная токенами: потолок ТАРИФА к ней не применяется
                // (см. комментарий у __qualityTier.clamp). Флаг едет вместе со
                // значением — глобального «сейчас платно» не существует.
                paid: opts.paid === true
            });
            report(slug, 'pipeline_mp3_ok', { engine: 'lamejs', size: blob.size });
            return blob;
        } catch(err) {
            if (err && err.name === 'AbortError') throw err;
            console.warn('[audio-pipeline] lamejs MP3 failed, falling back to ffmpeg:', err.message);
            report(slug, 'pipeline_mp3_fallback', { engine: 'lamejs', err: (err.message || '').substring(0, 120) });
            if (!asWav) asWav = audioBufferToWav(input);
        }
    }

    _abortIfSignaled(signal);
    if (!asWav) asWav = (input instanceof Blob) ? input : audioBufferToWav(input);

    try {
        var blob2 = await encodeMP3ViaFFmpeg(asWav, bitrate, slug, undefined, signal, opts.onProgress, opts.paid === true);
        report(slug, 'pipeline_mp3_ok', { engine: 'ffmpeg', size: blob2.size });
        return blob2;
    } catch(err) {
        if (err && err.name === 'AbortError') {
            report(slug, 'pipeline_mp3_cancelled', { engine: 'ffmpeg' });
            throw err;
        }
        report(slug, 'pipeline_mp3_failed', { engine: 'ffmpeg', err: (err.message || '').substring(0, 120) });
        throw err;
    }
}

window.AudioPipeline = {
    stretch: stretch,
    encodeMP3: encodeMP3,
    audioBufferToWav: audioBufferToWav,
    // float → Int16 тулам со своим писателем: тот же квантователь, что у WAV сайта.
    pcm16Quantizer: pcm16Quantizer,
    decodeAtSourceRate: decodeAtSourceRate,
    config: function(){ return Object.assign({}, window.__AUDIO_CONFIG || {}); },
    // Exposed so tools can pre-flight a stretch op themselves (e.g. naive
    // playbackRate fallback paths that bypass `stretch()` and would otherwise
    // hit `Array buffer allocation failed` from OfflineAudioContext on
    // 7+ MB lossy files / 100+ s decoded buffers / 2 GB devices.
    isStretchSafe: stretchIsSafe,
    // Pure policy ('wsola' | 'phase-vocoder') for a stretch request and a
    // content verdict — exposed for callers that own their own tempo path
    // (/speed stretches the original FILE with FFmpeg) and for the guard test.
    stretchEngineFor: stretchEngineFor,
    contentFromDetect: contentFromDetect,
    contentOf: function(buffer, opts){ return contentOf(buffer, opts || {}, (opts && opts.slug) || null); },
    estimateStretchPeakBytes: estimateStretchPeakBytes,
    deviceMemoryCapBytes: deviceMemoryCapBytes,
    version: '1.0.0'
};

})();
